import { useEffect, useState } from "react";
import { useModal } from "../context/ModalContext";
import {
  getCompanyAddressById,
  createCompanyAddress,
  updateCompanyAddress,
} from "../services/companyService";
import Loading from "./Loading";
import { Save, Loader2, ArrowLeft, ChevronsRight } from "lucide-react";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

function CompanyAddressForm({
  companyId,
  onNext,
  onBack,
  versionId,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);

  const [formData, setFormData] = useState({
    registered: {
      registeredAddress: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      id: 0,
    },
    operating: {
      registeredAddress: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      id: 0,
    },
    sameAsRegistered: false,
    version: versionId,
  });

  const { showModal } = useModal();
  const toast = useToast();

  const fetchAddressData = async () => {
    try {
      setLoading(true);

      const fetchVersion = formData.version;
      let isFallback = false;

      // 1. Try fetching data for the current version
      let res = await getCompanyAddressById(companyId, fetchVersion);

      // 2. If no data found, and version > 1, fallback to previous version
      if ((!res || res.length === 0) && fetchVersion > 1) {
        res = await getCompanyAddressById(companyId, fetchVersion - 1);
        isFallback = true;
        setFallbackUsed(true);
      }

      if (res) {
        const registered = res.find((a) => a.addressType?.id === 1) || {};
        const operating = res.find((a) => a.addressType?.id === 2) || {};

        const registeredData = {
          registeredAddress: registered.registeredAddress || "",
          country: registered.country || "",
          state: registered.state || "",
          city: registered.city || "",
          zipCode: registered.zipCode || "",
          ...(isFallback ? {} : { id: registered.id || 0 }),
        };

        const operatingData = {
          registeredAddress: operating.registeredAddress || "",
          country: operating.country || "",
          state: operating.state || "",
          city: operating.city || "",
          zipCode: operating.zipCode || "",
          ...(isFallback ? {} : { id: operating.id || 0 }),
        };

        const areSame =
          registeredData.registeredAddress ===
            operatingData.registeredAddress &&
          registeredData.country === operatingData.country &&
          registeredData.state === operatingData.state &&
          registeredData.city === operatingData.city &&
          registeredData.zipCode === operatingData.zipCode;

        setFormData((prev) => ({
          ...prev,
          registered: registeredData,
          operating: areSame
            ? {
                ...registeredData,
                ...(isFallback ? {} : { id: operatingData.id }),
              }
            : operatingData,
          sameAsRegistered: areSame,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch address info", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddressData();
    console.log(isEditable);
  }, [companyId]);

  const handleInputChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
    if (formData.sameAsRegistered) {
      setFormData((prev) => ({
        ...prev,
        operating: formData.sameAsRegistered
          ? { ...prev.registered, id: prev.operating.id }
          : prev.operating,
      }));
    }
    setIsChanged(true);
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      sameAsRegistered: checked,
      operating: checked
        ? { ...prev.registered, id: prev.operating.id }
        : prev.operating,
    }));
    setIsChanged(true);
  };

  const saveAddress = async (address, addressType) => {
    const payload = {
      registeredAddress: address.registeredAddress,
      country: address.country,
      state: address.state,
      city: address.city,
      zipCode: address.zipCode,
      addressType,
      version: formData.version,
      companyId: parseInt(companyId),
    };

    if (address.id > 0) {
      payload.id = address.id;
    }

    const response = payload.id
      ? await updateCompanyAddress(payload)
      : await createCompanyAddress(payload);

    if (!response || !response.status) {
      throw new Error(`Failed to save address type ${addressType}`);
    }
  };

  const handleSave = async (showModalAfterSave, goNext) => {
    try {
      setSaving(true);
      await saveAddress(formData.registered, 1);
      await saveAddress(formData.operating, 2);

      if (showModalAfterSave) {
        // showModal(
        //   "Address Saved",
        //   "Company address has been saved successfully."
        // );
        toast.success("Company address has been saved successfully.");
      }

      if (goNext && onNext) {
        onNext();
      }

      setIsChanged(false);
    } catch (err) {
      console.error("Error saving address info:", err);
      // showModal(
      //   "Save Failed",
      //   "Something went wrong while saving the address."
      // );
      toast.error("Something went wrong while saving the address.");
    } finally {
      setSaving(false);
    }
  };

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="px-10 max-w-6xl mx-auto">
      <div className="bg-white p-8 rounded-lg shadow">
        <h2 className="text-2xl font-semibold mb-8">Company Address</h2>

        {/* Registered Address */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Registered Address</h3>
          <div className="grid grid-cols-2 gap-6">
            <input
              className={`col-span-2 px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.registered.registeredAddress}
              onChange={(e) =>
                handleInputChange(
                  "registered",
                  "registeredAddress",
                  e.target.value
                )
              }
              placeholder="Registered Address"
              disabled={!isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.registered.country}
              onChange={(e) =>
                handleInputChange("registered", "country", e.target.value)
              }
              placeholder="Country"
              disabled={!isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.registered.state}
              onChange={(e) =>
                handleInputChange("registered", "state", e.target.value)
              }
              placeholder="State"
              disabled={!isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.registered.city}
              onChange={(e) =>
                handleInputChange("registered", "city", e.target.value)
              }
              placeholder="City"
              disabled={!isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.registered.zipCode}
              type="number"
              onChange={(e) =>
                handleInputChange("registered", "zipCode", e.target.value)
              }
              placeholder="Postal Zip"
              disabled={!isEditable}
            />
          </div>
        </div>

        {/* Operating Address */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Operating Address</h3>
          <div className="grid grid-cols-2 gap-6">
            <input
              className={`col-span-2 px-4 py-2 border rounded ${
                formData.sameAsRegistered || !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.operating.registeredAddress}
              onChange={(e) => {
                if (!formData.sameAsRegistered) {
                  handleInputChange(
                    "operating",
                    "registeredAddress",
                    e.target.value
                  );
                }
              }}
              placeholder="Operating Address"
              disabled={formData.sameAsRegistered || !isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                formData.sameAsRegistered || !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.operating.country}
              onChange={(e) => {
                if (!formData.sameAsRegistered) {
                  handleInputChange("operating", "country", e.target.value);
                }
              }}
              placeholder="Country"
              disabled={formData.sameAsRegistered || !isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                formData.sameAsRegistered || isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.operating.state}
              onChange={(e) => {
                if (!formData.sameAsRegistered) {
                  handleInputChange("operating", "state", e.target.value);
                }
              }}
              placeholder="State"
              disabled={formData.sameAsRegistered || !isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                formData.sameAsRegistered || !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.operating.city}
              onChange={(e) => {
                if (!formData.sameAsRegistered) {
                  handleInputChange("operating", "city", e.target.value);
                }
              }}
              placeholder="City"
              disabled={formData.sameAsRegistered || !isEditable}
            />
            <input
              className={`px-4 py-2 border rounded ${
                formData.sameAsRegistered || !isEditable ? "bg-gray-100" : ""
              }`}
              value={formData.operating.zipCode}
              onChange={(e) => {
                if (!formData.sameAsRegistered) {
                  handleInputChange("operating", "zipCode", e.target.value);
                }
              }}
              placeholder="Postal Zip"
              disabled={formData.sameAsRegistered || !isEditable}
            />
          </div>
        </div>

        {/* Checkbox */}
        <div className="mb-6">
          <label className="flex items-center gap-2 text-gray-700">
            <input
              type="checkbox"
              checked={formData.sameAsRegistered || !isEditable}
              onChange={handleCheckboxChange}
            />
            Same as registered address
          </label>
        </div>

        {/* --- Buttons Section using the new component --- */}
        <FormNavigationButtons
          onBack={onBack}
          onNext={onNext}
          handleSaveOnly={() => handleSave(true, false)}
          handleSaveAndContinue={() => handleSave(true, true)}
          isEditable={isEditable}
          isChanged={isChanged}
          saving={saving}
          fallbackUsed={fallbackUsed}
          currentStep={currentStep} // Pass current step
          totalSteps={totalSteps} // Pass total steps
        />
        {/* --- End Buttons Section --- */}

        {/* Buttons */}
        <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {isEditable && (
            <button
              type="button"
              onClick={() => handleSave(true, false)}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          )}

          {isEditable && (
            <button
              type="button"
              onClick={() => handleSave(true, true)}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
          )}

          <button
            type="button"
            onClick={onNext}
            disabled={saving || (isEditable && isChanged && !fallbackUsed)}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || (isEditable && isChanged && !fallbackUsed)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
            }`}
          >
            <ChevronsRight className="w-4 h-4" />
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default CompanyAddressForm;
